/**
 * InsightCacheConfig Component
 *
 * Admin component for Insight Cache configuration:
 * - General settings (enabled, embeddings, notifications)
 * - Insight type management (CRUD)
 * - Statistics overview
 *
 * @module components/admin/InsightCacheConfig
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Brain,
  RefreshCw,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Check,
  Settings,
  BarChart3,
  Tag,
  Sparkles,
  Bell,
  Search,
  ChevronDown,
  ChevronUp,
  Hash,
  TrendingUp,
  Database,
  Layers,
} from 'lucide-react';
import {
  TimeScaleSelector,
  StatisticsFilters,
  InsightAreaChart,
  InsightBarChart,
  InsightDonutChart,
} from './charts';
import {
  InsightBrowserFilters,
  InsightBrowserTable,
  InsightDetailModal,
  BulkDeleteBar,
} from './insight-browser';

// ============================================
// Constants
// ============================================

const API_BASE = '/api';

// ============================================
// API Functions (will be connected to real API)
// ============================================

async function fetchInsightConfig() {
  const res = await fetch(`${API_BASE}/insight-config`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch config');
  const result = await res.json();
  return result.data || result;
}

async function updateInsightConfig(config) {
  const res = await fetch(`${API_BASE}/insight-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(config)
  });
  if (!res.ok) throw new Error('Failed to update config');
  const result = await res.json();
  return result.data || result;
}

async function fetchInsightTypes() {
  const res = await fetch(`${API_BASE}/insight-types`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch types');
  return res.json();
}

async function createInsightType(typeData) {
  const res = await fetch(`${API_BASE}/insight-types`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(typeData)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to create type');
  }
  const result = await res.json();
  return result.data || result;
}

async function updateInsightType(id, typeData) {
  const res = await fetch(`${API_BASE}/insight-types/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(typeData)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to update type');
  }
  const result = await res.json();
  return result.data || result;
}

async function deleteInsightType(id) {
  const res = await fetch(`${API_BASE}/insight-types/${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to delete type');
  }
  return { success: true };
}

async function fetchInsightStats() {
  const res = await fetch(`${API_BASE}/insight-stats`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch stats');
  const result = await res.json();
  return result.data || result;
}

async function fetchInsightTimeseries({ period = '7d', projectId, agentId } = {}) {
  const params = new URLSearchParams({ period });
  if (projectId) params.append('projectId', projectId);
  if (agentId) params.append('agentId', agentId);

  const res = await fetch(`${API_BASE}/insights/timeseries?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch timeseries');
  const result = await res.json();
  return result.data || result;
}

async function fetchProjects() {
  const res = await fetch(`${API_BASE}/projects`, { credentials: 'include' });
  if (!res.ok) return [];
  const result = await res.json();
  return Array.isArray(result) ? result : (result.data || []);
}

async function fetchAgents() {
  const res = await fetch(`${API_BASE}/agents`, { credentials: 'include' });
  if (!res.ok) return [];
  const result = await res.json();
  return Array.isArray(result) ? result : (result.data || []);
}

async function retryPendingEmbeddings(limit = 20) {
  const res = await fetch(`${API_BASE}/insights/retry-embeddings?limit=${limit}`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to retry embeddings');
  }
  return res.json();
}

async function fetchInsightsList({ page = 1, limit = 20, search, agentId, projectId, type, hasEmbedding, sortBy = 'createdAt', sortOrder = 'desc' } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit), sortBy, sortOrder });
  if (search) params.append('search', search);
  if (agentId) params.append('agentId', agentId);
  if (projectId) params.append('projectId', projectId);
  if (type) params.append('type', type);
  if (hasEmbedding !== null && hasEmbedding !== undefined) params.append('hasEmbedding', String(hasEmbedding));

  const res = await fetch(`${API_BASE}/insights?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch insights');
  return res.json();
}

async function deleteInsight(id) {
  const res = await fetch(`${API_BASE}/insights/${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Failed to delete insight');
  return res.json();
}

async function bulkDeleteInsights(ids) {
  const res = await fetch(`${API_BASE}/insights/bulk`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ ids })
  });
  if (!res.ok) throw new Error('Failed to delete insights');
  return res.json();
}

async function semanticSearch({ query, threshold = 0.7, limit = 10, projectId, agentId, cachedEmbedding } = {}) {
  const res = await fetch(`${API_BASE}/insights/semantic-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ query, threshold, limit, projectId, agentId, cachedEmbedding })
  });
  if (!res.ok) throw new Error('Failed to search');
  return res.json();
}

// ============================================
// Consolidation API Functions
// ============================================

async function triggerConsolidationScan(projectId = null) {
  const res = await fetch(`${API_BASE}/insights/scan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ projectId })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to trigger scan');
  }
  const result = await res.json();
  return result.data || result;
}

async function fetchScanReports({ page = 1, limit = 20, projectId } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (projectId) params.set('projectId', projectId);
  const res = await fetch(`${API_BASE}/insights/scan-reports?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch reports');
  const result = await res.json();
  return result.data || result;
}

async function fetchScanReportDetail(reportId) {
  const res = await fetch(`${API_BASE}/insights/scan-reports/${reportId}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch report detail');
  const result = await res.json();
  return result.data || result;
}

async function restoreInsight(insightId) {
  const res = await fetch(`${API_BASE}/insights/${insightId}/restore`, {
    method: 'PUT',
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to restore insight');
  }
  const result = await res.json();
  return result.data || result;
}

// ============================================
// Tab Constants
// ============================================

const TABS = [
  { id: 'stats', label: 'Stats', icon: BarChart3 },
  { id: 'browser', label: 'Browser', icon: Database },
  { id: 'search', label: 'Semantic Search', icon: Search },
  { id: 'consolidation', label: 'Consolidation', icon: Layers },
  { id: 'config', label: 'Config', icon: Settings },
];

// ============================================
// Sub-Components
// ============================================

/**
 * Toggle switch component
 */
function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${checked ? 'bg-indigo-600' : 'bg-gray-600'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${checked ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  );
}

/**
 * Settings Section component
 */
function SettingsSection({ config, onConfigChange, isUpdating }) {
  const handleToggle = (key) => {
    onConfigChange({ ...config, [key]: !config[key] });
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-indigo-500/10">
          <Settings className="w-5 h-5 text-indigo-400" />
        </div>
        <h2 className="text-lg font-semibold text-white">General Settings</h2>
      </div>

      <div className="space-y-4">
        {/* Insight Cache Enabled */}
        <div className="flex items-center justify-between py-2">
          <div>
            <p className="text-sm font-medium text-gray-200">Insight Cache enabled</p>
            <p className="text-xs text-gray-500">Enable extraction and storage of agent insights</p>
          </div>
          <Toggle
            checked={config.enabled}
            onChange={() => handleToggle('enabled')}
            disabled={isUpdating}
          />
        </div>

        {/* Embeddings Enabled */}
        <div className="flex items-center justify-between py-2 border-t border-gray-700/50">
          <div>
            <p className="text-sm font-medium text-gray-200">Semantic Search (Embeddings)</p>
            <p className="text-xs text-gray-500">Generate vector embeddings for semantic search</p>
          </div>
          <Toggle
            checked={config.embeddingsEnabled}
            onChange={() => handleToggle('embeddingsEnabled')}
            disabled={isUpdating || !config.enabled}
          />
        </div>

        {/* High Severity Notifications */}
        <div className="flex items-center justify-between py-2 border-t border-gray-700/50">
          <div>
            <p className="text-sm font-medium text-gray-200">High-Severity Notifications</p>
            <p className="text-xs text-gray-500">Notify project leads for high-severity insights</p>
          </div>
          <Toggle
            checked={config.highSeverityNotifications}
            onChange={() => handleToggle('highSeverityNotifications')}
            disabled={isUpdating || !config.enabled}
          />
        </div>

        {/* Default Search Threshold */}
        <div className="flex items-center justify-between py-2 border-t border-gray-700/50">
          <div>
            <p className="text-sm font-medium text-gray-200">Default Search Threshold</p>
            <p className="text-xs text-gray-500">Minimum similarity score for semantic search (0.0 - 1.0)</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={config.defaultSearchThreshold ?? 0.35}
              onChange={(e) => onConfigChange({ ...config, defaultSearchThreshold: parseFloat(e.target.value) })}
              disabled={isUpdating || !config.enabled}
              className="w-24 accent-indigo-500"
            />
            <span className="text-sm text-gray-300 w-10">{(config.defaultSearchThreshold ?? 0.35).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Consolidation Settings Section component
 */
function ConsolidationSettingsSection({ config, onConfigChange, isUpdating }) {
  const handleChange = (key, value) => {
    onConfigChange({ ...config, [key]: value });
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-indigo-500/10">
          <Layers className="w-5 h-5 text-indigo-400" />
        </div>
        <h2 className="text-lg font-semibold text-white">Consolidation Settings</h2>
      </div>

      <div className="space-y-4">
        {/* Batch Size */}
        <div className="flex items-center justify-between py-2">
          <div>
            <p className="text-sm font-medium text-gray-200">Batch Size</p>
            <p className="text-xs text-gray-500">Insights per LLM call (5-100)</p>
          </div>
          <input
            type="number"
            min="5"
            max="100"
            value={config.consolidationBatchSize ?? 20}
            onChange={(e) => handleChange('consolidationBatchSize', parseInt(e.target.value) || 20)}
            disabled={isUpdating}
            className="w-20 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-white text-center focus:outline-none focus:border-indigo-500"
          />
        </div>

        {/* Max Tokens */}
        <div className="flex items-center justify-between py-2 border-t border-gray-700/50">
          <div>
            <p className="text-sm font-medium text-gray-200">Max Tokens</p>
            <p className="text-xs text-gray-500">Token limit for LLM response (1024-32768)</p>
          </div>
          <input
            type="number"
            min="1024"
            max="32768"
            step="1024"
            value={config.consolidationMaxTokens ?? 8192}
            onChange={(e) => handleChange('consolidationMaxTokens', parseInt(e.target.value) || 8192)}
            disabled={isUpdating}
            className="w-24 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-white text-center focus:outline-none focus:border-indigo-500"
          />
        </div>

        {/* LLM Model */}
        <div className="flex items-center justify-between py-2 border-t border-gray-700/50">
          <div>
            <p className="text-sm font-medium text-gray-200">LLM Model</p>
            <p className="text-xs text-gray-500">OpenRouter model ID</p>
          </div>
          <input
            type="text"
            value={config.consolidationLlmModel ?? 'google/gemini-2.0-flash-001'}
            onChange={(e) => handleChange('consolidationLlmModel', e.target.value)}
            disabled={isUpdating}
            className="w-64 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-indigo-500"
          />
        </div>

        {/* Quality Threshold */}
        <div className="flex items-center justify-between py-2 border-t border-gray-700/50">
          <div>
            <p className="text-sm font-medium text-gray-200">Quality Threshold</p>
            <p className="text-xs text-gray-500">Insights with score ≤ threshold are auto-invalidated (1-5)</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="1"
              max="5"
              step="1"
              value={config.consolidationQualityThreshold ?? 2}
              onChange={(e) => handleChange('consolidationQualityThreshold', parseInt(e.target.value))}
              disabled={isUpdating}
              className="w-24 accent-indigo-500"
            />
            <span className="text-sm text-gray-300 w-6 text-center">{config.consolidationQualityThreshold ?? 2}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Type badge with color indicator
 */
function TypeBadge({ name, color, enabled = true }) {
  return (
    <span
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium
        ${enabled ? 'opacity-100' : 'opacity-50'}
      `}
      style={{
        backgroundColor: `${color}20`,
        color: color,
        border: `1px solid ${color}40`
      }}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      {name}
    </span>
  );
}

/**
 * Insight Type Form (inline)
 */
function TypeForm({ type, onSubmit, onCancel, isSubmitting }) {
  const [name, setName] = useState(type?.name || '');
  const [displayName, setDisplayName] = useState(type?.displayName || '');
  const [description, setDescription] = useState(type?.description || '');
  const [color, setColor] = useState(type?.color || '#6366f1');

  const isEditing = !!type?.id;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: name.toLowerCase().replace(/\s+/g, '-'),
      displayName: displayName.trim(),
      description: description.trim(),
      color
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Name (slug)</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="e.g., custom-type"
            disabled={isEditing}
            className="
              w-full px-3 py-2 rounded-md text-sm
              bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-indigo-500
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Display Name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Custom Type"
            className="
              w-full px-3 py-2 rounded-md text-sm
              bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-indigo-500
            "
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description"
            className="
              w-full px-3 py-2 rounded-md text-sm
              bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-indigo-500
            "
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-10 h-10 rounded cursor-pointer bg-transparent border-0"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="
                flex-1 px-3 py-2 rounded-md text-sm font-mono
                bg-gray-900 border border-gray-700 text-gray-100
                focus:outline-none focus:ring-2 focus:ring-indigo-500
              "
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-3 py-1.5 rounded-md text-sm text-gray-400 hover:text-white hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !name.trim() || !displayName.trim()}
          className="
            flex items-center gap-2 px-3 py-1.5 rounded-md text-sm
            bg-indigo-600 text-white hover:bg-indigo-500
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isEditing ? 'Save' : 'Create'}
        </button>
      </div>
    </form>
  );
}

/**
 * Insight Types Section
 */
function TypesSection({ types, onTypeCreate, onTypeUpdate, onTypeDelete, isLoading }) {
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleSubmit = async (typeData) => {
    setIsSubmitting(true);
    try {
      if (editingType) {
        await onTypeUpdate(editingType.id, typeData);
      } else {
        await onTypeCreate(typeData);
      }
      setShowForm(false);
      setEditingType(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await onTypeDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingType(null);
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-500/10">
            <Tag className="w-5 h-5 text-green-400" />
          </div>
          <h2 className="text-lg font-semibold text-white">Insight Types</h2>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-gray-700 hover:bg-gray-600 text-gray-300"
          >
            <Plus className="w-4 h-4" />
            Add Type
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-4">
          <TypeForm
            type={editingType}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {types.map((type) => (
            <div
              key={type.id}
              className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-gray-600 transition-colors"
            >
              <div className="flex items-center gap-3">
                <TypeBadge name={type.displayName} color={type.color} enabled={type.enabled} />
                <span className="text-sm text-gray-500">{type.description}</span>
              </div>
              <div className="flex items-center gap-2">
                {type.isDefault && (
                  <span className="text-xs text-gray-500 italic">Default</span>
                )}
                <button
                  type="button"
                  onClick={() => handleEdit(type)}
                  className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </button>
                {!type.isDefault && (
                  <button
                    type="button"
                    onClick={() => handleDelete(type.id)}
                    disabled={deletingId === type.id}
                    className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-gray-700 disabled:opacity-50"
                    title="Delete"
                  >
                    {deletingId === type.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Statistics Section
 */
function StatsSection({ stats, isLoading, onRetryEmbeddings, isRetrying }) {
  if (isLoading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  const pendingCount = stats.pendingEmbeddings ?? 0;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-purple-500/10">
          <BarChart3 className="w-5 h-5 text-purple-400" />
        </div>
        <h2 className="text-lg font-semibold text-white">Statistics</h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/50">
          <p className="text-2xl font-bold text-white">{stats.totalInsights ?? 0}</p>
          <p className="text-xs text-gray-500">Total Insights</p>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/50">
          <p className="text-2xl font-bold text-green-400">{stats.thisWeek ?? 0}</p>
          <p className="text-xs text-gray-500">This Week</p>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-amber-400">{pendingCount}</p>
              <p className="text-xs text-gray-500">Pending Embeddings</p>
            </div>
            {pendingCount > 0 && (
              <button
                onClick={onRetryEmbeddings}
                disabled={isRetrying}
                title="Retry pending embeddings"
                className="p-2 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 transition-colors disabled:opacity-50"
              >
                {isRetrying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/50">
          <p className="text-2xl font-bold text-indigo-400">{stats.topAgents?.length || 0}</p>
          <p className="text-xs text-gray-500">Active Agents</p>
        </div>
      </div>

      {/* By Type */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-400 mb-3">By Type</h3>
        <div className="flex flex-wrap gap-2">
          {Array.isArray(stats.byType) && stats.byType.map((item) => (
            <span
              key={item.type}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs bg-gray-700/50 text-gray-300"
            >
              {item.type} <span className="text-gray-500">({item.count})</span>
            </span>
          ))}
        </div>
      </div>

      {/* Top Agents */}
      <div>
        <h3 className="text-sm font-medium text-gray-400 mb-3">Top Agents</h3>
        <div className="space-y-2">
          {Array.isArray(stats.topAgents) && stats.topAgents.map((agent, index) => (
            <div key={agent.name} className="flex items-center justify-between text-sm">
              <span className="text-gray-300">
                <span className="text-gray-500 mr-2">{index + 1}.</span>
                {agent.name}
              </span>
              <span className="text-gray-500">{agent.count} insights</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Charts Section - Timeseries, By Agent, By Type
 */
function ChartsSection({ projects, agents }) {
  const [period, setPeriod] = useState('7d');
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadChartData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchInsightTimeseries({
        period,
        projectId: selectedProjectId,
        agentId: selectedAgentId,
      });
      setChartData(data);
    } catch (err) {
      console.error('Failed to load chart data:', err);
      setChartData(null);
    } finally {
      setIsLoading(false);
    }
  }, [period, selectedProjectId, selectedAgentId]);

  useEffect(() => {
    loadChartData();
  }, [loadChartData]);

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-500/10">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
          </div>
          <h2 className="text-lg font-semibold text-white">Insight Charts</h2>
        </div>

        <div className="flex items-center gap-4">
          <StatisticsFilters
            agents={agents}
            projects={projects}
            selectedAgentId={selectedAgentId}
            selectedProjectId={selectedProjectId}
            onAgentChange={setSelectedAgentId}
            onProjectChange={setSelectedProjectId}
          />
          <TimeScaleSelector value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* Metadata */}
      {chartData?.metadata && (
        <div className="mb-4 text-xs text-gray-500">
          {chartData.metadata.startDate} - {chartData.metadata.endDate}
          {' | '}
          {chartData.metadata.totalCount} Insights total
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Area Chart - Full Width */}
        <div className="col-span-2">
          <InsightAreaChart
            data={chartData?.timeseries || []}
            isLoading={isLoading}
          />
        </div>

        {/* Bar Chart - By Agent */}
        <InsightBarChart
          data={chartData?.byAgent || []}
          isLoading={isLoading}
        />

        {/* Donut Chart - By Type */}
        <InsightDonutChart
          data={chartData?.byType || []}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

/**
 * Browser Tab Component
 */
function BrowserTab({ agents, projects, insightTypes }) {
  const [filters, setFilters] = useState({
    search: '',
    agentId: null,
    projectId: null,
    type: null,
    hasEmbedding: null,
  });
  const [insights, setInsights] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadInsights = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchInsightsList({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        agentId: filters.agentId || undefined,
        projectId: filters.projectId || undefined,
        type: filters.type || undefined,
        hasEmbedding: filters.hasEmbedding,
        sortBy,
        sortOrder,
      });
      setInsights(result.insights || result.data || []);
      setPagination(prev => ({
        ...prev,
        total: result.pagination?.total || 0,
        totalPages: result.pagination?.totalPages || 0,
      }));
    } catch (err) {
      console.error('Failed to load insights:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, sortBy, sortOrder]);

  // Debounced filter change
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      loadInsights();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Immediate reload on other filter changes
  useEffect(() => {
    loadInsights();
  }, [filters.agentId, filters.projectId, filters.type, filters.hasEmbedding, pagination.page, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleDelete = async (id) => {
    setIsDeleting(true);
    try {
      await deleteInsight(id);
      await loadInsights();
      setSelectedIds(prev => prev.filter(i => i !== id));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      await bulkDeleteInsights(selectedIds);
      await loadInsights();
      setSelectedIds([]);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <InsightBrowserFilters
        filters={filters}
        onFilterChange={setFilters}
        agents={agents}
        projects={projects}
        insightTypes={insightTypes}
      />

      <InsightBrowserTable
        insights={insights}
        pagination={pagination}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onRowClick={setSelectedInsight}
        isLoading={isLoading}
      />

      <InsightDetailModal
        insight={selectedInsight}
        onClose={() => setSelectedInsight(null)}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <BulkDeleteBar
        selectedCount={selectedIds.length}
        onDelete={handleBulkDelete}
        onClear={() => setSelectedIds([])}
        isDeleting={isDeleting}
      />
    </div>
  );
}

/**
 * Semantic Search Tab Component (Placeholder)
 */
const THRESHOLD_STORAGE_KEY = 'insight-search-threshold';
const DEFAULT_THRESHOLD = 0.35;

function SemanticSearchTab({ agents, projects, config, onConfigChange }) {
  const [query, setQuery] = useState('');
  const [threshold, setThreshold] = useState(() => {
    // Priority: localStorage > agent config > default
    const stored = localStorage.getItem(THRESHOLD_STORAGE_KEY);
    if (stored) return parseFloat(stored);
    if (config?.defaultSearchThreshold !== undefined) return config.defaultSearchThreshold;
    return DEFAULT_THRESHOLD;
  });
  const [limit, setLimit] = useState(10);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  const [showAgentFormat, setShowAgentFormat] = useState(false);
  const [cachedEmbedding, setCachedEmbedding] = useState(null);
  const [timings, setTimings] = useState(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setIsLoading(true);
    try {
      const result = await semanticSearch({ query, threshold, limit });
      setResults(result.results || []);
      setCachedEmbedding(result.queryEmbedding || null);
      setTimings({
        embeddingTime: result.queryEmbeddingTime,
        searchTime: result.searchTime,
      });
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Live threshold update with debounce
  useEffect(() => {
    if (!cachedEmbedding || !query.trim()) return;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const result = await semanticSearch({ query, threshold, limit, cachedEmbedding });
        setResults(result.results || []);
        setTimings({
          embeddingTime: 0,
          searchTime: result.searchTime,
        });
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setIsLoading(false);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [threshold, limit, cachedEmbedding]);

  const getSimilarityColor = (score) => {
    if (score >= 0.85) return 'bg-emerald-500';
    if (score >= 0.70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      {/* Search Input */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-4">
          <Search className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-medium text-gray-300">Semantic Search</span>
        </div>
        <div className="flex gap-3">
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter your search query..."
            rows={2}
            className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none"
          />
          <button
            onClick={handleSearch}
            disabled={isLoading || !query.trim()}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-md disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
        </div>

        {/* Controls */}
        <div className="mt-4 flex items-center gap-6">
          {/* Threshold Slider */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">Threshold:</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={threshold}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setThreshold(val);
                localStorage.setItem(THRESHOLD_STORAGE_KEY, val.toString());
              }}
              className="w-32 accent-indigo-500"
            />
            <span className="text-sm text-gray-300 w-10">{threshold.toFixed(2)}</span>
          </div>

          {/* Limit */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Limit:</span>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="px-2 py-1 bg-gray-900 border border-gray-700 rounded text-xs text-white"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          {/* Set as Agent Default Button */}
          <button
            onClick={async () => {
              setIsSavingDefault(true);
              try {
                await onConfigChange({ ...config, defaultSearchThreshold: threshold });
              } finally {
                setIsSavingDefault(false);
              }
            }}
            disabled={isSavingDefault || config?.defaultSearchThreshold === threshold}
            className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Save current threshold as agent default"
          >
            {isSavingDefault ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : config?.defaultSearchThreshold === threshold ? (
              <Check className="w-3 h-3 text-green-400" />
            ) : (
              <Save className="w-3 h-3" />
            )}
            Set as Default
          </button>

          {/* Format Toggle */}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-500">Format:</span>
            <button
              onClick={() => setShowAgentFormat(false)}
              className={`px-2 py-1 rounded text-xs ${!showAgentFormat ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-400'}`}
            >
              Human
            </button>
            <button
              onClick={() => setShowAgentFormat(true)}
              className={`px-2 py-1 rounded text-xs ${showAgentFormat ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-400'}`}
            >
              Agent XML
            </button>
          </div>
        </div>

        {/* Timings */}
        {timings && (
          <div className="mt-3 text-xs text-gray-500">
            {timings.embeddingTime > 0 && `Embedding: ${timings.embeddingTime}ms | `}
            Search: {timings.searchTime}ms
          </div>
        )}
      </div>

      {/* Results */}
      <div className="space-y-3">
        {results.length === 0 && !isLoading && query && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
            <Search className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No results found</p>
            <p className="text-sm text-gray-500 mt-1">Try lowering the threshold or changing your query</p>
          </div>
        )}

        {results.map((result, index) => (
          <div key={result.insight?.id || index} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            {/* Score Bar */}
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${getSimilarityColor(result.similarity)}`}
                  style={{ width: `${result.similarity * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-300">
                {(result.similarity * 100).toFixed(1)}%
              </span>
            </div>

            {/* Content */}
            {showAgentFormat ? (
              <pre className="text-xs text-gray-300 font-mono bg-gray-900/50 p-3 rounded overflow-x-auto">
                {result.formattedResponse || `<insight type="${result.insight?.type}">\n  ${result.insight?.content}\n</insight>`}
              </pre>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300">
                    {result.insight?.type}
                  </span>
                  <span className="text-xs text-gray-500">
                    {result.insight?.agent?.displayName || result.insight?.agent?.name || 'Unknown Agent'}
                  </span>
                </div>
                <p className="text-sm text-gray-300">{result.insight?.content}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Consolidation Tab Component
// ============================================

const LAST_SEEN_REPORT_KEY = 'insight-consolidation-last-seen';

function ConsolidationTab({ projects }) {
  // State
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProjectId, setScanProjectId] = useState('');
  const [error, setError] = useState(null);
  const [restoringIds, setRestoringIds] = useState(new Set());

  // Race condition protection
  const reportRequestRef = useRef(0);

  // Load reports
  const loadReports = useCallback(async () => {
    setIsLoadingReports(true);
    setError(null);
    try {
      const data = await fetchScanReports();
      setReports(data.reports || data || []);
      // Update last seen timestamp
      localStorage.setItem(LAST_SEEN_REPORT_KEY, new Date().toISOString());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoadingReports(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Trigger scan
  const handleTriggerScan = async () => {
    setIsScanning(true);
    setError(null);
    try {
      const result = await triggerConsolidationScan(scanProjectId || null);
      // Reload reports to get the new one
      await loadReports();
      // If we got a reportId, select it
      if (result.reportId) {
        handleSelectReport(result.reportId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsScanning(false);
    }
  };

  // Select report for detail view
  const handleSelectReport = async (reportId) => {
    const currentRequest = ++reportRequestRef.current;
    setIsLoadingDetail(true);
    setError(null);
    try {
      const detail = await fetchScanReportDetail(reportId);
      // Ignore stale responses
      if (currentRequest !== reportRequestRef.current) return;
      setSelectedReport(detail);
    } catch (err) {
      if (currentRequest !== reportRequestRef.current) return;
      setError(err.message);
    } finally {
      if (currentRequest === reportRequestRef.current) {
        setIsLoadingDetail(false);
      }
    }
  };

  // Restore insight
  const handleRestore = async (insightId) => {
    setRestoringIds(prev => new Set(prev).add(insightId));
    try {
      await restoreInsight(insightId);
      // Update the selectedReport to reflect the change
      if (selectedReport) {
        setSelectedReport(prev => ({
          ...prev,
          details: {
            ...prev.details,
            invalidated: (prev.details?.invalidated || []).filter(i => i.insightId !== insightId)
          }
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setRestoringIds(prev => {
        const next = new Set(prev);
        next.delete(insightId);
        return next;
      });
    }
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Status badge
  const getStatusBadge = (status) => {
    const styles = {
      running: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-emerald-500/20 text-emerald-400',
      failed: 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.completed}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Scan Trigger */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-500/10">
              <Layers className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-white">Consolidation Scan</h3>
              <p className="text-xs text-gray-500">Analyze insights for duplicates, quality, and type normalization</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={scanProjectId}
              onChange={(e) => setScanProjectId(e.target.value)}
              disabled={isScanning}
              className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-gray-300 focus:outline-none focus:border-indigo-500"
            >
              <option value="">All Projects</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button
              onClick={handleTriggerScan}
              disabled={isScanning}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-md disabled:opacity-50"
            >
              {isScanning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Run Scan
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-4 rounded-md bg-red-500/10 border border-red-500/30 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Reports List & Detail */}
      <div className="grid grid-cols-12 gap-6">
        {/* Reports List */}
        <div className="col-span-4 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-white">Scan Reports</h3>
              <button
                onClick={loadReports}
                disabled={isLoadingReports}
                className="text-gray-400 hover:text-gray-300"
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingReports ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {isLoadingReports ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : reports.length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm">
                No scan reports yet
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {reports.map((report) => (
                  <button
                    key={report.id}
                    onClick={() => handleSelectReport(report.id)}
                    className={`w-full p-4 text-left hover:bg-gray-700/50 transition-colors ${
                      selectedReport?.id === report.id ? 'bg-gray-700/50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500">{formatRelativeTime(report.createdAt)}</span>
                      {getStatusBadge(report.status)}
                    </div>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {report.summary?.invalidated > 0 && (
                        <div className="px-2 py-1 rounded bg-red-500/10 text-center">
                          <span className="text-xs text-red-400">{report.summary.invalidated}</span>
                          <span className="text-[10px] text-red-400/70 block">invalid</span>
                        </div>
                      )}
                      {report.summary?.duplicatesFound > 0 && (
                        <div className="px-2 py-1 rounded bg-amber-500/10 text-center">
                          <span className="text-xs text-amber-400">{report.summary.duplicatesFound}</span>
                          <span className="text-[10px] text-amber-400/70 block">dupes</span>
                        </div>
                      )}
                      {report.summary?.typeChanges > 0 && (
                        <div className="px-2 py-1 rounded bg-blue-500/10 text-center">
                          <span className="text-xs text-blue-400">{report.summary.typeChanges}</span>
                          <span className="text-[10px] text-blue-400/70 block">typed</span>
                        </div>
                      )}
                      {report.summary?.tagsAdded > 0 && (
                        <div className="px-2 py-1 rounded bg-emerald-500/10 text-center">
                          <span className="text-xs text-emerald-400">{report.summary.tagsAdded}</span>
                          <span className="text-[10px] text-emerald-400/70 block">tagged</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      {report.insightsScanned || 0} insights scanned
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Report Detail */}
        <div className="col-span-8 bg-gray-800 border border-gray-700 rounded-lg">
          {isLoadingDetail ? (
            <div className="flex items-center justify-center h-[500px]">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : !selectedReport ? (
            <div className="flex items-center justify-center h-[500px] text-gray-500 text-sm">
              Select a report to view details
            </div>
          ) : (
            <div className="h-[500px] overflow-y-auto">
              {/* Report Header */}
              <div className="p-4 border-b border-gray-700 sticky top-0 bg-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">
                        Scan Report
                      </span>
                      {getStatusBadge(selectedReport.status)}
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(selectedReport.createdAt).toLocaleString()} • {selectedReport.insightsScanned || 0} insights
                    </span>
                  </div>
                  {selectedReport.summary?.costUsd && (
                    <div className="text-xs text-gray-400">
                      Cost: ${selectedReport.summary.costUsd.toFixed(4)} • {selectedReport.summary.tokensUsed?.toLocaleString()} tokens
                    </div>
                  )}
                </div>
              </div>

              {/* Detail Sections */}
              <div className="p-4 space-y-4">
                {/* Invalidated Insights */}
                {selectedReport.details?.invalidated?.length > 0 && (
                  <ReportSection
                    title="Invalidated"
                    icon={<AlertCircle className="w-4 h-4 text-red-400" />}
                    items={selectedReport.details.invalidated}
                    color="red"
                    renderItem={(item) => (
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300">{item.type}</span>
                            <span className="text-xs text-gray-500 truncate">{item.reasoning || item.reason}</span>
                          </div>
                          {item.content && (
                            <p className="text-sm text-gray-300 truncate">{item.content}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleRestore(item.insightId)}
                          disabled={restoringIds.has(item.insightId)}
                          className="ml-4 px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded disabled:opacity-50"
                        >
                          {restoringIds.has(item.insightId) ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            'Restore'
                          )}
                        </button>
                      </div>
                    )}
                  />
                )}

                {/* Duplicates */}
                {selectedReport.details?.duplicates?.length > 0 && (
                  <ReportSection
                    title="Duplicates Found"
                    icon={<Layers className="w-4 h-4 text-amber-400" />}
                    items={selectedReport.details.duplicates}
                    color="amber"
                    renderItem={(item) => (
                      <div>
                        <p className="text-sm text-gray-300 mb-1">{item.reasoning || 'Semantic duplicate detected'}</p>
                        <p className="text-xs text-gray-500">Duplicate of: {item.duplicateOf || item.invalidatedId}</p>
                      </div>
                    )}
                  />
                )}

                {/* Type Changes */}
                {selectedReport.details?.typeChanges?.length > 0 && (
                  <ReportSection
                    title="Type Normalizations"
                    icon={<Tag className="w-4 h-4 text-blue-400" />}
                    items={selectedReport.details.typeChanges}
                    color="blue"
                    renderItem={(item) => (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-600 text-gray-400 line-through">{item.from}</span>
                          <span className="text-gray-500">→</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">{item.to}</span>
                        </div>
                        {item.content && (
                          <p className="text-sm text-gray-300 truncate mb-1">{item.content}</p>
                        )}
                        {item.reasoning && (
                          <p className="text-xs text-gray-500">{item.reasoning}</p>
                        )}
                      </div>
                    )}
                  />
                )}

                {/* Tags Added */}
                {selectedReport.details?.tagsAdded?.length > 0 && (
                  <ReportSection
                    title="Tags Added"
                    icon={<Hash className="w-4 h-4 text-emerald-400" />}
                    items={selectedReport.details.tagsAdded}
                    color="emerald"
                    renderItem={(item) => (
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {(item.addedTags || item.tags || []).map((tag, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">
                              #{tag}
                            </span>
                          ))}
                        </div>
                        {item.content && (
                          <p className="text-sm text-gray-300 truncate">{item.content}</p>
                        )}
                      </div>
                    )}
                  />
                )}

                {/* Obsolete */}
                {selectedReport.details?.obsolete?.length > 0 && (
                  <ReportSection
                    title="Potentially Obsolete"
                    icon={<TrendingUp className="w-4 h-4 text-gray-400" />}
                    items={selectedReport.details.obsolete}
                    color="gray"
                    renderItem={(item) => (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300">{item.type}</span>
                          <span className="text-xs text-gray-500">{item.reasoning || item.reason}</span>
                        </div>
                        {item.content && (
                          <p className="text-sm text-gray-300 truncate">{item.content}</p>
                        )}
                        {item.filePath && (
                          <p className="text-xs text-gray-500 mt-1 font-mono">{item.filePath}</p>
                        )}
                      </div>
                    )}
                  />
                )}

                {/* Empty State */}
                {!selectedReport.details?.invalidated?.length &&
                 !selectedReport.details?.duplicates?.length &&
                 !selectedReport.details?.typeChanges?.length &&
                 !selectedReport.details?.tagsAdded?.length &&
                 !selectedReport.details?.obsolete?.length && (
                  <div className="py-8 text-center text-gray-500 text-sm">
                    {selectedReport.status === 'running'
                      ? 'Scan in progress...'
                      : 'No changes detected in this scan'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Report Section Component for grouping details
 */
function ReportSection({ title, icon, items, color, renderItem }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const borderColors = {
    red: 'border-l-red-500',
    amber: 'border-l-amber-500',
    blue: 'border-l-blue-500',
    emerald: 'border-l-emerald-500',
    gray: 'border-l-gray-500'
  };

  return (
    <div className={`border border-gray-700 rounded-lg overflow-hidden border-l-2 ${borderColors[color]}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-gray-700/30 hover:bg-gray-700/50"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-white">{title}</span>
          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-700 text-gray-300">
            {items.length}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {isExpanded && (
        <div className="divide-y divide-gray-700">
          {items.map((item, idx) => (
            <div key={item.insightId || idx} className="p-3">
              {renderItem(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export default function InsightCacheConfig() {
  // Tab state
  const [activeTab, setActiveTab] = useState('stats');

  // State
  const [config, setConfig] = useState({ enabled: true, embeddingsEnabled: true, highSeverityNotifications: true });
  const [types, setTypes] = useState([]);
  const [stats, setStats] = useState({});
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRetryingEmbeddings, setIsRetryingEmbeddings] = useState(false);
  const [error, setError] = useState(null);
  const [unreadReportCount, setUnreadReportCount] = useState(0);

  // Check for unread consolidation reports
  useEffect(() => {
    const checkUnreadReports = async () => {
      try {
        const lastSeen = localStorage.getItem(LAST_SEEN_REPORT_KEY);
        const data = await fetchScanReports({ limit: 50 });
        const reports = data.reports || data || [];
        if (lastSeen) {
          const lastSeenDate = new Date(lastSeen);
          const unreadCount = reports.filter(r => new Date(r.createdAt) > lastSeenDate).length;
          setUnreadReportCount(unreadCount);
        } else if (reports.length > 0) {
          setUnreadReportCount(reports.length);
        }
      } catch (err) {
        console.error('Failed to check unread reports:', err);
      }
    };
    checkUnreadReports();
  }, []);

  // Clear unread count when visiting consolidation tab
  useEffect(() => {
    if (activeTab === 'consolidation') {
      setUnreadReportCount(0);
    }
  }, [activeTab]);

  // Load data
  const loadData = useCallback(async () => {
    setError(null);

    // Load projects and agents for filters
    try {
      const [projectsData, agentsData] = await Promise.all([
        fetchProjects(),
        fetchAgents()
      ]);
      setProjects(projectsData);
      setAgents(agentsData);
    } catch (err) {
      console.error('Failed to load projects/agents:', err);
    }

    try {
      setIsLoadingConfig(true);
      const configData = await fetchInsightConfig();
      setConfig(configData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoadingConfig(false);
    }

    try {
      setIsLoadingTypes(true);
      const typesData = await fetchInsightTypes();
      setTypes(Array.isArray(typesData) ? typesData : (typesData.data || []));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoadingTypes(false);
    }

    try {
      setIsLoadingStats(true);
      const statsData = await fetchInsightStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleConfigChange = useCallback(async (newConfig) => {
    setIsUpdating(true);
    try {
      await updateInsightConfig(newConfig);
      setConfig(newConfig);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const handleTypeCreate = useCallback(async (typeData) => {
    const newType = await createInsightType(typeData);
    setTypes(prev => [...prev, newType]);
  }, []);

  const handleTypeUpdate = useCallback(async (id, typeData) => {
    const updatedType = await updateInsightType(id, typeData);
    setTypes(prev => prev.map(t => t.id === id ? { ...t, ...updatedType } : t));
  }, []);

  const handleTypeDelete = useCallback(async (id) => {
    await deleteInsightType(id);
    setTypes(prev => prev.filter(t => t.id !== id));
  }, []);

  const handleRetryEmbeddings = useCallback(async () => {
    setIsRetryingEmbeddings(true);
    try {
      const result = await retryPendingEmbeddings(20);
      // Refresh stats to show updated pending count
      const statsData = await fetchInsightStats();
      setStats(statsData);
      console.log('Retry embeddings result:', result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRetryingEmbeddings(false);
    }
  }, []);

  // Render
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-500/10">
            <Brain className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Insight Cache</h1>
            <p className="text-sm text-gray-400">Configure insight extraction and storage</p>
          </div>
        </div>
        <button
          type="button"
          onClick={loadData}
          disabled={isLoadingConfig || isLoadingTypes || isLoadingStats}
          className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${(isLoadingConfig || isLoadingTypes || isLoadingStats) ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-700">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const showBadge = tab.id === 'consolidation' && unreadReportCount > 0;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors
                ${isActive
                  ? 'border-indigo-500 text-indigo-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                }
              `}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {showBadge && (
                <span className="px-1.5 py-0.5 text-xs rounded-full bg-indigo-500 text-white">
                  {unreadReportCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 rounded-md bg-red-500/10 border border-red-500/30 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          {/* Stats Section */}
          <StatsSection
            stats={stats}
            isLoading={isLoadingStats}
            onRetryEmbeddings={handleRetryEmbeddings}
            isRetrying={isRetryingEmbeddings}
          />

          {/* Charts Section */}
          <ChartsSection
            projects={projects}
            agents={agents}
          />
        </div>
      )}

      {activeTab === 'config' && (
        <div className="space-y-6">
          {/* Settings Section */}
          <SettingsSection
            config={config}
            onConfigChange={handleConfigChange}
            isUpdating={isUpdating}
          />

          {/* Consolidation Settings Section */}
          <ConsolidationSettingsSection
            config={config}
            onConfigChange={handleConfigChange}
            isUpdating={isUpdating}
          />

          {/* Types Section */}
          <TypesSection
            types={types}
            onTypeCreate={handleTypeCreate}
            onTypeUpdate={handleTypeUpdate}
            onTypeDelete={handleTypeDelete}
            isLoading={isLoadingTypes}
          />
        </div>
      )}

      {activeTab === 'browser' && (
        <BrowserTab
          agents={agents}
          projects={projects}
          insightTypes={types}
        />
      )}

      {activeTab === 'search' && (
        <SemanticSearchTab
          agents={agents}
          projects={projects}
          config={config}
          onConfigChange={handleConfigChange}
        />
      )}

      {activeTab === 'consolidation' && (
        <ConsolidationTab
          projects={projects}
        />
      )}
    </div>
  );
}

// Named Exports
export {
  SettingsSection,
  TypesSection,
  StatsSection,
  ChartsSection,
  TypeBadge,
  TypeForm,
  Toggle
};
